import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import MainLayout from './layouts/MainLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import BillingDashboard from './pages/billing/Dashboard';
import CustomerList from './pages/billing/CustomerList';
import CustomerDetails from './pages/billing/CustomerDetails';
import ProductList from './pages/billing/ProductList';
import SubscriptionList from './pages/billing/SubscriptionList';
import SubscriptionForm from './pages/billing/SubscriptionForm';
import SubscriptionView from './pages/billing/SubscriptionView';
import InvoiceList from './pages/billing/InvoiceList';
import QuoteList from './pages/billing/QuoteList';
import QuoteForm from './pages/billing/QuoteForm';
import QuoteView from './pages/billing/QuoteView';
import PublicQuoteView from './pages/billing/PublicQuoteView';
import FeedbackList from './pages/billing/FeedbackList';
import TicketMappingPage from './pages/billing/TicketMappingPage';
import BillingReports from './pages/billing/Reports';
import SystemLogs from './pages/billing/SystemLogs';
import SettingsPage from './pages/billing/SettingsPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import TeamLoginPage from './pages/auth/TeamLoginPage';
import TeamMembersPage from './pages/auth/TeamMembersPage';
import RolesPage from './pages/auth/RolesPage';
import EmbedEntryPage from './pages/auth/EmbedEntryPage';
import ApiKeysPage from './pages/integrations/ApiKeysPage';
import IframeIntegrationPage from './pages/integrations/IframeIntegrationPage';
import ApiDocsPage from './pages/integrations/ApiDocsPage';
import authService from './api/authService';
import { getToken, isAutoAuthPending, removeApiKey, removeToken, removeTeamEmail, removeUser, setToken, setUser, setAutoAuthPending } from './Utility/Utility';

const useUrlAutoAuth = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const ranRef = useRef('');

  const params = new URLSearchParams(location.search);
  const email = params.get('email');
  const password = params.get('password');
  const loginMode = params.get('loginMode');
  const hasParams = !!(email && password && loginMode);

  if (hasParams && !isAutoAuthPending() && ranRef.current !== `${email}|${password}|${loginMode}`) {
    setAutoAuthPending(true);
  }

  useLayoutEffect(() => {
    if (!email || !password || !loginMode) return;

    const runKey = `${email}|${password}|${loginMode}`;
    if (ranRef.current === runKey) return;
    ranRef.current = runKey;

    const resolveTarget = () =>
      location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/team-login'
        ? '/'
        : location.pathname;

    const buildUserInfoFromAuth = (rawUser, permissions = null) => {
      if (!rawUser) return null;
      const perms = permissions || {
        allowedPages: rawUser.allowedPages || [],
        readOnly: rawUser.readOnly !== undefined ? rawUser.readOnly : false,
        actionPermissions: rawUser.actionPermissions || {},
        customPermissions: rawUser.customPermissions || {},
        tableActions: rawUser.tableActions || {}
      };
      return {
        id: rawUser.id,
        name: rawUser.name,
        email: rawUser.email,
        admin: rawUser.admin,
        role: rawUser.role,
        roleTemplateId: rawUser.roleTemplateId || '',
        roleTemplateName: rawUser.roleTemplateName || '',
        companyName: rawUser.companyName || '',
        loginType: rawUser.loginType,
        permissions: perms
      };
    };

    const onAuthSuccess = (token, rawUser) => {
      setToken(token);
      if (rawUser) {
        const userInfo = buildUserInfoFromAuth(rawUser);
        if (userInfo) setUser(userInfo);
      }
      setAutoAuthPending(false);
      navigate(resolveTarget(), { replace: true });
    };

    const runAutoAuth = async () => {
      setAutoAuthPending(true);
      try {
        if (getToken()) {
          removeToken();
          removeApiKey();
          removeTeamEmail();
          removeUser();
        }

        const company = params.get('company');
        const phone = params.get('phone');
        const ownerName = params.get('ownerName');

        if (loginMode === 'owner') {
          try {
            const regRes = await authService.register({
              name: ownerName || '',
              companyName: company || '',
              email,
              phone: phone || '',
              password
            });
            if (regRes?.success && regRes.token) {
              onAuthSuccess(regRes.token, regRes.user);
              return;
            }
          } catch (_regErr) {
            // email already exists or other register error → fall through to login
          }
          try {
            const loginRes = await authService.login({ email, password });
            if (loginRes?.success && loginRes.token) {
              onAuthSuccess(loginRes.token, loginRes.user);
              return;
            }
          } catch (_loginErr) {
            // login failed → fall through, finally clears pending
          }
        } else if (loginMode === 'team') {
          const role = (params.get('role') || 'agent').toLowerCase();
          const rawCompany = company || '';
          const adminParam = (params.get('admin') || '').toLowerCase();
          const ownerEmailParam = (params.get('ownerEmail') || '').toLowerCase();
          const companyNameParam = params.get('companyName') || '';

          const admin = adminParam
            || (ownerEmailParam && ownerEmailParam.includes('@') ? ownerEmailParam : '')
            || (rawCompany && rawCompany.includes('@') ? rawCompany.toLowerCase() : '');
          const companyNameVal = companyNameParam
            || (rawCompany && !rawCompany.includes('@') ? rawCompany : '')
            || '';

          try {
            const onboardPayload = {
              email,
              password,
              role,
              company: rawCompany || '',
              companyName: companyNameVal,
              ownerName: ownerName || '',
              ownerEmail: ownerEmailParam,
              admin,
              phone: phone || '',
              name: ownerName || params.get('name') || email
            };
            const ownerPwd = params.get('ownerPassword');
            if (ownerPwd) onboardPayload.ownerPassword = ownerPwd;

            const onboardRes = await authService.autoTeamOnboard(onboardPayload);
            if (onboardRes?.success && onboardRes.token) {
              onAuthSuccess(onboardRes.token, onboardRes.user);
              return;
            }
          } catch (_onboardErr) {
            // if auto-onboard fails (e.g. role invalid, missing owner email), try plain teamLogin
          }
          try {
            const loginRes = await authService.teamLogin({ email, password });
            if (loginRes?.success && loginRes.token) {
              onAuthSuccess(loginRes.token, loginRes.user);
              return;
            }
          } catch (_teamErr) {
            // fall through
          }
        }
      } catch (_err) {
        // swallow top-level errors silently
      } finally {
        setAutoAuthPending(false);
      }
    };

    runAutoAuth();
  }, [location.search, location.pathname, navigate, email, password, loginMode, params]);
};

const AppShell = () => {
  useUrlAutoAuth();
  return (
    <Routes>
        {/* Public Routes */}
        <Route path="/public/quotes/:publicId" element={<PublicQuoteView />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/team-login" element={<TeamLoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/embed" element={<EmbedEntryPage />} />

        {/* Private Routes with Sidebar */}
        <Route path="*" element={
          <ProtectedRoute>
            <MainLayout>
              <Routes>
                <Route path="/" element={<BillingDashboard />} />
                <Route path="/customers" element={<CustomerList />} />
                <Route path="/billing/customers/:id" element={<CustomerDetails />} />
                <Route path="/products" element={<ProductList />} />
                <Route path="/quotation" element={<QuoteList />} />
                <Route path="/quotations" element={<QuoteList />} />
                <Route path="/billing/quotes/create" element={<QuoteForm />} />
                <Route path="/billing/quotes/edit/:id" element={<QuoteForm />} />
                <Route path="/billing/quotes/view/:id" element={<QuoteView />} />
                <Route path="/billing/feedback" element={<FeedbackList />} />
                <Route path="/ticket-mappings" element={<TicketMappingPage />} />
                <Route path="/subscriptions" element={<SubscriptionList />} />
                <Route path="/billing/subscriptions/create" element={<SubscriptionForm />} />
                <Route path="/billing/subscriptions/edit/:id" element={<SubscriptionForm />} />
                <Route path="/billing/subscriptions/view/:id" element={<SubscriptionView />} />
                <Route path="/invoices" element={<InvoiceList />} />
                <Route path="/billing-reports" element={<BillingReports />} />
                <Route path="/system-logs" element={<SystemLogs />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/team-members" element={<TeamMembersPage />} />
                <Route path="/roles" element={<RolesPage />} />
                <Route path="/api-keys" element={<ApiKeysPage />} />
                <Route path="/iframe-integrations" element={<IframeIntegrationPage />} />
                <Route path="/api-docs" element={<ApiDocsPage />} />
              </Routes>
            </MainLayout>
          </ProtectedRoute>
        } />
      </Routes>
    );
  };

function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

export default App;
